/**
 * Batch = the same settings applied to every file (R1/R2). The master state is the one the user
 * edits on the selected photo; each other item receives a translation of it that respects its own
 * pixel size. With the ordered-ops model (2026-09-06) that is almost free: resize ops carry an
 * INTENT and derive their pixels from each file's own input; crops are fractions of their input;
 * rotation and flips copy verbatim. The one policy left is what a crop means on a photo of a
 * different size: the same region (fractions, only meaningful on a same-size photo) or the same
 * SHAPE centred (default).
 */
import { EditState, Size, Op, initialState, sizeAfter, cropRectPx, centredRectWithAspect, stepOutput } from './edit-stack';

export const BATCH_LIMIT = 50;

export type CropPolicy = 'same-region' | 'centre-aspect';

export function applyMasterToItem(master: EditState, itemSource: Size, cropPolicy: CropPolicy = 'centre-aspect'): EditState {
    const s = initialState(itemSource);
    const sameSize = itemSource.width === master.source.width && itemSource.height === master.source.height;
    const ops: Op[] = [];
    let itemInput: Size = { ...s.source }; let k = 1;   // this file's own size and cumulative scale at each step
    master.ops.forEach((op, i) => {
        let o: Op = op;
        if (op.type === 'crop' && !(cropPolicy === 'same-region' && sameSize)) {
            // the same shape (pixel aspect of the master's box at this step), centred on this photo's input
            const r = cropRectPx(op, sizeAfter(master, i));
            const c = centredRectWithAspect(itemInput, r.width / Math.max(1, r.height));
            o = { ...op, f: { x: c.x / itemInput.width, y: c.y / itemInput.height, w: c.width / itemInput.width, h: c.height / itemInput.height } };
        }
        ops.push(o);
        const r = stepOutput(o, itemInput, k); itemInput = r.out; k = r.k;
    });
    return { ...s, ops };
}
